<template>
  <LoadingCard :loading="submitting" class="px-6 py-4">
    <div class="flex items-center justify-between gap-4 mb-6">
      <div>
        <h3 class="leading-tight text-sm font-bold">{{ card.title }}</h3>
        <p class="text-sm text-gray-500 mt-2" v-if="hasAvailableDates">
          Perioadă disponibilă: {{ formattedAvailableDates }}
        </p>
        <p class="text-sm text-gray-500 mt-2" v-else>
          Nu există încă aplicări înregistrate pentru export.
        </p>
      </div>
    </div>

    <div class="grid md:grid-cols-5 gap-4">
      <div>
        <label class="inline-block text-80 pt-0 leading-tight text-sm font-bold mb-2">
          Raport
        </label>
        <SelectControl
          class="w-full"
          size="sm"
          :options="reportOptions"
          :selected="form.report"
          @change="handleReportChange"
        />
      </div>

      <div>
        <label class="inline-block text-80 pt-0 leading-tight text-sm font-bold mb-2">
          Format
        </label>
        <SelectControl
          class="w-full"
          size="sm"
          :options="formatOptions"
          :selected="form.format"
          @change="value => (form.format = normalizeValue(value))"
        />
      </div>

      <div>
        <label class="inline-block text-80 pt-0 leading-tight text-sm font-bold mb-2">
          Filtrare
        </label>
        <SelectControl
          class="w-full"
          size="sm"
          :options="filterModeOptions"
          :selected="form.filterMode"
          @change="value => (form.filterMode = normalizeValue(value))"
        />
      </div>

      <div v-if="form.filterMode === 'preset'">
        <label class="inline-block text-80 pt-0 leading-tight text-sm font-bold mb-2">
          Interval
        </label>
        <SelectControl
          class="w-full"
          size="sm"
          :options="selectedReportRanges"
          :selected="form.range"
          @change="value => (form.range = normalizeValue(value))"
        />
      </div>

      <template v-else>
        <div>
          <label class="inline-block text-80 pt-0 leading-tight text-sm font-bold mb-2">
            Data start
          </label>
          <input
            v-model="form.startDate"
            type="date"
            class="w-full flex form-control form-input form-input-bordered"
            :min="card.minDate || undefined"
            :max="card.maxDate || undefined"
          />
        </div>

        <div>
          <label class="inline-block text-80 pt-0 leading-tight text-sm font-bold mb-2">
            Data sfârșit
          </label>
          <input
            v-model="form.endDate"
            type="date"
            class="w-full flex form-control form-input form-input-bordered"
            :min="form.startDate || card.minDate || undefined"
            :max="card.maxDate || undefined"
          />
        </div>
      </template>
    </div>

    <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-6">
      <p class="text-sm text-gray-500">
        {{ selectionSummary }}
      </p>

      <div class="flex flex-col sm:flex-row gap-3">
        <Button
          variant="solid"
          @click="prepareReport"
          :disabled="!canSubmit"
        >
          Generează raportul
        </Button>

        <a
          v-if="generatedDownloadUrl"
          :href="generatedDownloadUrl"
          target="_blank"
          rel="noopener"
          class="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 h-9 text-sm font-bold text-gray-700 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring focus:ring-primary-200"
        >
          Descarcă raportul
        </a>
      </div>
    </div>

    <div
      v-if="generatedDownloadUrl"
      class="mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800"
    >
      Raportul este pregătit pentru intervalul selectat. Apasă pe Descarcă raportul.
    </div>
  </LoadingCard>
</template>

<script>
import { Button } from 'laravel-nova-ui'

export default {
  name: 'JobApplicationReportGeneratorCard',

  components: {
    Button,
  },

  props: {
    card: {
      type: Object,
      required: true,
    },
  },

  data() {
    const defaultReport = this.card.defaultReport || this.card.reports?.[0]?.value
    const defaultRange = this.card.reports?.[0]?.ranges?.[0]?.value || 'ALL'

    return {
      submitting: false,
      generatedDownloadUrl: '',
      form: {
        report: defaultReport,
        format: this.card.defaultFormat || 'xlsx',
        filterMode: this.card.defaultFilterMode || 'preset',
        range: defaultRange,
        startDate: this.card.minDate || '',
        endDate: this.card.maxDate || '',
      },
    }
  },

  computed: {
    hasAvailableDates() {
      return Boolean(this.card.minDate && this.card.maxDate)
    },

    formattedAvailableDates() {
      return `${this.card.minDate} - ${this.card.maxDate}`
    },

    reportOptions() {
      return this.card.reports || []
    },

    formatOptions() {
      return this.card.formats || []
    },

    filterModeOptions() {
      return [
        { value: 'preset', label: 'Interval rapid' },
        { value: 'custom', label: 'Interval personalizat' },
      ]
    },

    selectedReport() {
      return this.card.reports.find(report => report.value === this.form.report)
    },

    selectedReportRanges() {
      return this.selectedReport?.ranges || []
    },

    selectedRangeLabel() {
      return this.selectedReportRanges.find(range => range.value === this.form.range)?.label
    },

    canSubmit() {
      if (!this.hasAvailableDates) {
        return false
      }

      if (this.form.filterMode === 'custom') {
        return Boolean(
          this.form.startDate &&
            this.form.endDate &&
            this.form.startDate <= this.form.endDate
        )
      }

      return Boolean(this.form.range)
    },

    selectionSummary() {
      if (this.form.filterMode === 'custom') {
        return `Export ${this.selectedReport?.label || ''} pentru intervalul ${this.form.startDate || '...'} - ${this.form.endDate || '...'}.`
      }

      return `Export ${this.selectedReport?.label || ''} pentru ${this.selectedRangeLabel || 'intervalul selectat'}.`
    },
  },

  watch: {
    form: {
      handler() {
        this.generatedDownloadUrl = ''
      },
      deep: true,
    },
  },

  methods: {
    normalizeValue(value) {
      return value?.target?.value || value
    },

    handleReportChange(value) {
      this.form.report = this.normalizeValue(value)

      if (!this.selectedReportRanges.find(range => range.value === this.form.range)) {
        this.form.range = this.selectedReportRanges[0]?.value || 'ALL'
      }
    },

    buildDownloadUrl() {
      const basePath = `${this.card.baseDownloadPath}/${this.form.report}.${this.form.format}`
      const params = new URLSearchParams()

      if (this.form.filterMode === 'custom') {
        params.set('filter_mode', 'custom')
        params.set('start_date', this.form.startDate)
        params.set('end_date', this.form.endDate)
      } else {
        params.set('filter_mode', 'preset')
        params.set('range', this.form.range)
      }

      return `${basePath}?${params.toString()}`
    },

    prepareReport() {
      if (!this.canSubmit) {
        Nova.error('Completează intervalul înainte de generare.')
        return
      }

      this.submitting = true
      this.generatedDownloadUrl = this.buildDownloadUrl()

      window.setTimeout(() => {
        this.submitting = false
      }, 500)
    },
  },
}
</script>
